'use client';

import Link from 'next/link';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import GoogleSignInButton from 'src/components/GoogleSignInButton';
import MagicLinkForm from 'src/components/MagicLinkForm';
import WaitlistForm from 'src/components/WaitlistForm';
import { useAuth } from 'src/contexts/AuthContext';
import { useExtensionAuthHandler } from 'src/utils/extensionAuth';

const MiraLogo = () => (
  <svg width="96" height="30" viewBox="0 0 52 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.5759 1.35667L10.5401 0.187378L15.483 7.53556L13.7176 8.59444V11.0434H13.3643L8.5759 3.9161V1.35667Z" fill="currentColor"/>
    <path d="M4.40582 3.54183L6.36999 2.37254L11.313 9.72072L9.54753 10.7796V13.2286H9.19421L4.40582 6.10126V3.54183Z" fill="currentColor"/>
    <path d="M0.257812 5.72583L2.22198 4.55654L7.16495 11.9047L5.39952 12.9636V15.4126H5.0462L0.257812 8.28526V5.72583Z" fill="currentColor"/>
    <path d="M18.9513 12.0194V12.0204L18.9492 12.0194H18.9513V3.5407H20.8378V5.05501H20.9558C21.3293 4.05212 22.3114 3.42705 23.5884 3.42705C24.8654 3.42705 25.8277 3.97635 26.28 5.05501H26.3783C26.9091 3.97635 27.9895 3.42705 29.2862 3.42705C31.1727 3.42705 32.3513 4.60042 32.3513 6.62614V12.0204H30.3862V6.79661C30.3862 5.64219 29.6991 4.97924 28.5783 4.97924C27.3603 4.97924 26.6328 5.77378 26.6328 6.9292V12.0204H24.6677V6.66402C24.6677 5.66113 23.9412 4.97924 22.8992 4.97924C21.7991 4.97924 20.9144 5.86849 20.9144 7.11762V12.0194H18.9513Z" fill="currentColor"/>
    <path d="M33.8829 3.5407V12.0204H35.848V3.5407H33.8829Z" fill="currentColor"/>
    <path d="M37.4189 3.5407V12.0194H39.384V7.17444C39.384 5.62325 41.0149 4.73301 42.3902 5.20654L43.2543 3.78694C42.9004 3.5407 42.4099 3.40811 41.879 3.40811C40.6807 3.40811 39.7773 4.05212 39.4234 5.01713H39.3251V3.5407H37.4189Z" fill="currentColor"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M46.81 12.1909C45.2381 12.1909 43.8628 11.3395 43.274 9.95782C42.8611 8.99281 42.5672 7.08073 43.2936 5.64219C44.0004 4.22259 45.2195 3.42805 46.8297 3.42805C48.1656 3.42805 48.9118 3.97735 49.6196 5.11283H49.7375V3.5417H51.742V12.0214H49.7769V10.5071H49.6196C48.9511 11.5858 48.205 12.1919 46.81 12.1919V12.1909ZM49.4623 8.99181C49.0308 10.0705 48.3819 10.6387 47.2229 10.6387C46.0836 10.6387 45.3768 10.0894 44.9639 9.02969C44.7279 8.46245 44.61 7.40174 44.9442 6.55038C45.3364 5.56642 46.0836 4.9603 47.2229 4.9603C48.3623 4.9603 49.128 5.60431 49.4623 6.51249C49.7965 7.34491 49.5999 8.51828 49.4623 8.99181Z" fill="currentColor"/>
  </svg>
);

export default function LoginPage() {
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const { user, isLoading } = useAuth();
  const router = useRouter();
  const { handleAuthSuccess } = useExtensionAuthHandler();

  // Handle already logged in users
  useEffect(() => {
    if (!isLoading && user) {
      // Use shared extension auth handler
      handleAuthSuccess(() => {
        // Fallback: normal website login - redirect to app
        router.push('/app');
      });
    }
  }, [user, isLoading, router, handleAuthSuccess]);

  // Show loading while checking auth status
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
          <p className="text-sm text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render login form if user is logged in
  if (user) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-900 to-black text-white">
      {/* Navigation */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full">
        <div className="flex justify-between items-center">
          <Link href="/" className="flex items-center space-x-3">
            <span className="text-white"><MiraLogo /></span>
            <span className="bg-gray-800 text-gray-300 px-2 py-1 rounded-md text-xs font-medium border border-gray-700">BETA</span>
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-grow flex items-center justify-center w-full relative">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-1/4 -right-1/4 w-1/2 h-1/2 bg-gradient-radial from-gray-800/20 to-transparent rounded-full blur-3xl" />
          <div className="absolute -bottom-1/4 -left-1/4 w-1/2 h-1/2 bg-gradient-radial from-gray-800/20 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="w-full max-w-md px-4 sm:px-6 lg:px-8 py-12 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-2">Welcome Back</h2>
            <p className="text-gray-400 text-lg mb-8">
              Sign in to access your fact verification dashboard
            </p>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm py-8 px-4 shadow sm:rounded-xl sm:px-10 border border-gray-700/50">
            <div className="space-y-6">
              {/* Google Sign-In */}
              <GoogleSignInButton />
              
              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-600" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-800/50 text-gray-400">Or if you have an account</span>
                </div>
              </div>

              {/* Magic Link Form */}
              <MagicLinkForm />
              
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-400">
                  Don&apos;t have an account?{' '}
                  <button 
                    onClick={() => setShowWaitlistModal(true)}
                    className="text-white hover:text-gray-200 font-medium underline decoration-dotted underline-offset-2"
                  >
                    Join the waitlist
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="border-t border-gray-800 pt-6">
            <p className="text-center text-gray-500 text-sm">
              © {new Date().getFullYear()} Mira Fact Verifier. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Waitlist Modal */}
      {showWaitlistModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowWaitlistModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-white mb-2">Join the Waitlist</h3>
              <p className="text-gray-300 text-sm">
                Get early access to Mira Verify and be notified when your account is ready.
              </p>
            </div>
            
            <WaitlistForm />
          </div>
        </div>
      )}
    </div>
  );
} 